import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CalAngularService } from '@cvx/cal-angular';
import { ApprovalState, Client, DamageMechanismDto, FindingType, GeneralTabDto, ReportType, ShutDownType, API_BASE_URL, UpdateRequest, TlrTypeEnum, TaScopeEnum, EquipmentProcessServiceEnum, GetPeopleDataByEmailResponseDto } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { convertEnumValues, DisciplineOptions, ffsResultTypeOptions, FFSTypeOptions, filterTable, FindingTypeOptions, formatDate, getErrorMessage, notDefaultValue, noWhitespaceValidator, ReferenceTypeOptions, ShutDownTypeOptions, utcToLocal } from '@fdm/utils';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ApprovalFlowService, FieldHighlightService, FieldLocation, FormValidationService, NavigationService, ReportFormPage, ReportsBulkSaveService, ReportTask } from '../../../../services';
import { EmailDescriptionService } from '@fdm/services';
import { AddDamageMechanismDialogComponent } from './components/add-damage-mechanism-dialog/add-damage-mechanism-dialog.component';
import { ReportForm } from '../../report-form';
import { AppDateAdapter } from '../../../../../core/date/app-date-adapter';
import { APP_DATE_FORMATS } from '../../../../../core/date/app-date-formats';
import { AngularEditorModule, AngularEditorConfig } from '@kolkov/angular-editor';
import { createEditorConfigs } from '../../shared/richtext-editor-config-helper';
import { AuthService, Role, CommonUserService } from '@fdm/services';
import { ReferenceDialogComponent } from '../../../../../shared/dialogs/reference-dialog/reference-dialog.component';
import { environment } from '../../../../../../environments';
import { FilterResponse, TableColumnFilterComponent } from '@fdm/shared';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-general-tab',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,

    // Material
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    AngularEditorModule,
    MatAutocompleteModule,
    TableColumnFilterComponent,
    RouterLink
  ],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  templateUrl: './general-tab.component.html',
  styleUrl: './general-tab.component.scss',
})
export class GeneralTabComponent extends ReportForm implements OnInit {
  private static readonly EMAIL_AUTOCOMPLETE_DEBOUNCE_MS = 1200;

  private editorConfigs = createEditorConfigs(3, 'general');
  configEN: AngularEditorConfig = this.editorConfigs[0];
  configRU: AngularEditorConfig = this.editorConfigs[1];
  configKZ: AngularEditorConfig = this.editorConfigs[2];

  apiClient = inject(Client);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _authService = inject(AuthService);
  private _approvalFlow = inject(ApprovalFlowService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _navigationService = inject(NavigationService);
  protected _fieldHighlightService = inject(FieldHighlightService);
  protected FieldLocation = FieldLocation;
  loadingFields = signal<Record<string, boolean>>({});
  private _formValidationService = inject(FormValidationService);
  private emailDescriptionService = inject(EmailDescriptionService);
  private _commonUserService = inject(CommonUserService);

  highlightedField = this._fieldHighlightService.highlightedField;

  // Generic autocomplete options for email fields
  emailAutocompleteOptions = signal<{ [key: string]: string[] }>({});

  @Input() data: GeneralTabDto = new GeneralTabDto();
  @Input() readonly: boolean = false;

  reportStatusIndex = 0;
  private _fb: FormBuilder = new FormBuilder();
  public form!: FormGroup;
  private initiatorFormControl = this._fb.control('');
  private initiatorFullNameFormControl = this._fb.control('');
  private ownerFullNameFormControl = this._fb.control('');
  displayedColumns: string[] = [
    'select',
    'title',
    'code',
    'nameEN',
    'nameRU',
    'type',
  ];

  public override readonlyFields = [
    // 'initiator',
    'initiatorFullName',
    'ownerFullName',
    'closedOn',
    'revisions',
    'planApprovalDueDate',
    'daysForApproval',
    'created',
  ];

  protected displayNames: { [key: string]: string } = {
    reportType: 'Report Type',
    reportTitleEn: 'Report Title [EN]',
    reportTitleRu: 'Report Title [RU]',
    reportTitleKz: 'Report Title [KZ]',
    descriptionEn: 'Description [EN]',
    descriptionRu: 'Description [RU]',
    descriptionKz: 'Description [KZ]',
    discipline: 'Discipline',
    findingType: 'Finding Type',
    retirementDate: 'Retirement Date',
    initiator: 'Initiator',
    initiatorFullName: 'Initiator Full Name',
    owner: 'Owner',
    ownerFullName: 'Owner Full Name',
    closedOn: 'Closed On',
    resolutionTargetDate: 'Resolution Target Date',
    resolutionDateExplanation: 'Resolution Date Explanation',
    revisions: 'Revisions',
    ffsPerformed: 'FFS Performed',
    ffsResult: 'FFS Result',
    shutDownRequired: 'TA Required',
    value: 'Value, $',
    tonsOfOilEquivalent: 'Tons of Oil Equivalent',
    barrelsOfOilEquivalent: 'Barrels of Oil Equivalent',
    uniqueId: 'Unique ID',
    movedTo: 'Moved To',
    damageMechanisms: 'Damage Mechanisms',
    deviationFoundDate: 'Date when deviation found',
    planApprovalDueDate: 'Plan approval due date',
    daysForApproval: 'Days for approval',
    created: 'Creation date'
  };

  damageMechanismsDataSource = new MatTableDataSource<DamageMechanismDto>();
  selection = new SelectionModel<DamageMechanismDto>(true, []);
  @ViewChild(MatSort) sort!: MatSort;

  // Reference and TLR data sources
  referencesDataSource = new MatTableDataSource<any>();
  referencesSelection = new SelectionModel<any>(true, []);
  referencesDisplayedColumns: string[] = ['select', 'type', 'number', 'description', 'status', 'link', 'created'];
  isLoadingReferences = signal<boolean>(false);

  tlrEntriesDataSource = new MatTableDataSource<any>();
  tlrEntriesSelection = new SelectionModel<any>(true, []);
  tlrEntriesDisplayedColumns: string[] = ['select', 'tag', 'tlrType', 'taScope', 'equipmentProcessService', 'dateTlrInstalled', 'recommendedPermanentRepairDate'];
  isLoadingTlr = signal<boolean>(false);

  // Permission check for adding references/TLR
  canAddReferencesOrTlr = signal<boolean>(false);
  canEditDamageMechanisms = signal<boolean>(false);
  private _initiatorCanEditDamageMechanisms = false;

  public reportTypesList = Object.entries(convertEnumValues(ReportType));

  public findingTypes = FindingTypeOptions;

  public fitnessForServices = FFSTypeOptions;

  public ffsResults = ffsResultTypeOptions;

  public shutdownOptions = ShutDownTypeOptions;

  public disciplineOptions = DisciplineOptions;

  private tlrTypesMap = new Map(Object.entries(convertEnumValues(TlrTypeEnum)));
  private taScopesMap = new Map(Object.entries(convertEnumValues(TaScopeEnum)));
  private equipmentProcessServicesMap = new Map(Object.entries(convertEnumValues(EquipmentProcessServiceEnum)));

  isIntegrityThreat = false;
  isReliabilitThreat = false;

  today?: Date = undefined
  report = {};

  private _closeReport = toSignal(this._store.closeReport$);
  private _general = toSignal(this._store.general$);
  private currentStep = toSignal(this._store.page$);

  readonly ReportType = ReportType;

  public get reportType() {
    return this.form?.get('reportType') as FormControl<ReportType | null>;
  }

  public get findingType() {
    return this.form?.get('findingType') as FormControl<FindingType | null>;
  }

  public get discipline() {
    return this.form?.get('discipline') as FormControl<string | null>;
  }

  public get resolutionTargetDate() {
    return this.form?.get('resolutionTargetDate') as FormControl<Date | string>;
  }

  public get retirementDate() {
    return this.form?.get('retirementDate') as FormControl<Date | string>;
  }

  public get deviationFoundDate() {
    return this.form?.get('deviationFoundDate') as FormControl<Date | string>;
  }

  public get haveDamageMechanisms() {
    return this.form?.get('haveDamageMechanisms') as FormControl<boolean | null>;
  }

  public get owner() {
    return this.form.get('owner') as FormControl<string | null>;
  }

  public get closedOn() {
    return this.form.get('closedOn') as FormControl<string | null>;
  }

  public get step() {
    return ReportFormPage.GENERAL;
  }

  private _destroyRef = inject(DestroyRef);
  private _generalTabId: string | null = null;
  private _http = inject(HttpClient);
  private _taRequiredCanEdit = false;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private calService: CalAngularService,
  ) {
    super();

    this._fb = fb;

    this.disciplineOptions = DisciplineOptions.filter(item => !Boolean(item?.notAvailableForCreation));
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.initializeForm();
    this._initTriggers();
    this.loadReferences();
    this.loadTlrEntries();
  }

  ngAfterViewInit() {
    this.damageMechanismsDataSource.sort = this.sort;
  }

  protected override onPolicyOrFormStateChanged() {
    this.richTextFieldPolicy();
    this._handleTaskDeferalValidation();
    this._handleDamageMechanismValidation();
    this._applyTARequiredPolicy();
    this._updateDamageMechanismEditability();
  }

  private _updateDamageMechanismEditability(initiatorCanEdit = this._initiatorCanEditDamageMechanisms): void {
    this._initiatorCanEditDamageMechanisms = initiatorCanEdit;
    this.canEditDamageMechanisms.set(this.canEdit || initiatorCanEdit);

    if (!this.canEdit) {
      if (initiatorCanEdit) {
        this.haveDamageMechanisms?.enable({ emitEvent: false });
      } else {
        this.haveDamageMechanisms?.disable({ emitEvent: false });
      }
      this._updateGeneralStepValidity();
    }
  }

  richTextFieldPolicy() {
    this.configEN.editable = this.isFieldEditable('descriptionEn');
    this.configRU.editable = this.isFieldEditable('descriptionRu');
    this.configKZ.editable = this.isFieldEditable('descriptionKz');
  }

  private _applyTARequiredPolicy(): void {
    const control = this.form?.get('shutDownRequired');
    if (!control) return;

    if (this._taRequiredCanEdit) {
      control.enable({ emitEvent: false });
    } else if (!this.canEdit) {
      control.disable({ emitEvent: false });
    }
  }

  addDataToService() { }

  public isRequired(control: FormControl) {
    return control.hasValidator(Validators.required);
  }

  private _hasWhitespaceValidatedDiscipline(reportType: ReportType | null | undefined): boolean {
    return reportType === ReportType.IntegrityThreat
      || reportType === ReportType.ReliabilityThreat
      || reportType === ReportType.IntegrityDeficiency
      || reportType === ReportType.ReliabilityDeficiency
      || reportType === ReportType.AnomalyReport;
  }

  private _isTaskDeferralDisciplineRequired(reportType: ReportType | null | undefined): boolean {
    return reportType === ReportType.TaskDeferral;
  }

  protected isTaskDeferralReport(): boolean {
    return this.reportType?.getRawValue() === ReportType.TaskDeferral;
  }

  private _resolveOptionLabel(
    value: unknown,
    options: ReadonlyArray<{ value: unknown; label: string }>,
  ): string {
    if (value === null || value === undefined) {
      return 'n/a';
    }

    return options.find((option) => option.value === value)?.label ?? 'n/a';
  }

  protected getFindingTypeLabel(): string {
    return this._resolveOptionLabel(this.findingType?.getRawValue(), this.findingTypes);
  }

  protected getFfsPerformedLabel(): string {
    return this._resolveOptionLabel(this.form.get('ffsPerformed')?.getRawValue(), this.fitnessForServices);
  }

  protected getFfsResultLabel(): string {
    return this._resolveOptionLabel(this.form.get('ffsResult')?.getRawValue(), this.ffsResults);
  }

  protected getRetirementDateLabel(): string {
    const retirementDate = this.form.get('retirementDate')?.getRawValue();
    return formatDate(retirementDate) ?? 'n/a';
  }

  private _updateGeneralStepValidity(): void {
    this._store.setPageValidity({ page: this.step, isStepValid: this.form?.disabled || this.form?.valid });
  }

  private _updateDisciplineValidation(): void {
    const reportType = this.reportType?.getRawValue();

    if (this._hasWhitespaceValidatedDiscipline(reportType)) {
      this.discipline?.setValidators([Validators.required, noWhitespaceValidator()]);
    } else if (this._isTaskDeferralDisciplineRequired(reportType)) {
      this.discipline?.setValidators([Validators.required]);
    } else {
      this.discipline?.setValidators(null);
    }

    if (this.canEdit && !this.readonlyFields.includes('discipline')) {
      this.discipline?.enable({ emitEvent: false });
    }

    this.discipline?.updateValueAndValidity({ emitEvent: false });
    this._updateGeneralStepValidity();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.damageMechanismsDataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.damageMechanismsDataSource.data.forEach((row) => this.selection.select(row));
  }

  initializeForm(): void {
    this.form = this.fb.group({
      reportType: ['', Validators.required],
      reportTitleEn: ['', [Validators.required, Validators.maxLength(500), noWhitespaceValidator()]],
      reportTitleRu: ['', [Validators.maxLength(500)]],
      reportTitleKz: ['', [Validators.maxLength(500)]],
      descriptionEn: ['', [Validators.required, Validators.maxLength(20000), noWhitespaceValidator()]],
      descriptionRu: ['', [Validators.maxLength(20000)]],
      descriptionKz: ['', [Validators.maxLength(20000)]],
      discipline: [''],
      findingType: [null],
      retirementDate: [null],
      initiator: this.initiatorFormControl,
      initiatorFullName: this.initiatorFullNameFormControl, // don't see in Dto
      owner: ['', [Validators.required, Validators.email]],
      ownerFullName: this.ownerFullNameFormControl, // set after email validation
      closedOn: ['', { disabled: true }],
      resolutionTargetDate: [null, Validators.required],
      resolutionDateExplanation: ['', [Validators.required, noWhitespaceValidator()]],
      revisions: [{ value: '', disabled: true }], // don't see in Dto
      ffsPerformed: [null],
      ffsResult: [null],
      uniqueId: [''],
      shutDownRequired: [ShutDownType.No],
      value: [0, [Validators.min(-100000000), Validators.max(100000000)]],
      tonsOfOilEquivalent: [0, [Validators.min(-100000000), Validators.max(100000000)]],
      barrelsOfOilEquivalent: [0, [Validators.min(-100000000), Validators.max(100000000)]],
      haveDamageMechanisms: [null],
      deviationFoundDate: [null],
      planApprovalDueDate: [null],
      daysForApproval: [null],
      created: [null],
    });

    this.initiatorFormControl.setValue(
      this.calService.getAccount().username.toLowerCase() || null
    );

    combineLatest([this._authService.roles$, this._policy.canEditForPage$(this.step)])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([roles, _]) => {
        if (roles.includes(Role.Admin)) {
          this.form.get('initiator')?.enable({ emitEvent: false });
        } else {
          // this.initiatorFormControl.disable();
          this.form.get('initiator')?.disable({ emitEvent: false });
        }
      });

    this.closedOn.disable({ emitEvent: false });

    this.ownerFullNameFormControl.disable({ emitEvent: false });

    this.findingType.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((findingType) => {
        const retirementDateControl = this.form.get('retirementDate');
        if (findingType === FindingType.TimeDependentCondition) {
          retirementDateControl?.setValidators([Validators.required]);
        } else {
          retirementDateControl?.setValidators(null);
        }
        retirementDateControl?.updateValueAndValidity();
      });

    this.reportType.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((reportType) => {
        this.isIntegrityThreat = reportType === ReportType.IntegrityThreat;
        this.isReliabilitThreat = reportType === ReportType.ReliabilityThreat;

        this._checkDeviationFoundValidation(reportType);

        // need to check why sometimes passing empty report type
        if (reportType) {
          this._store.setReportType(reportType);
        }

        this._handleDamageMechanismValidation();
        this._handleTaskDeferalValidation();
      });

    this.form.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._updateGeneralStepValidity();
      });

    this._handleTaskDeferalValidation();
    this._updateDisciplineValidation();
    this._updateGeneralStepValidity();
    this._formValidationService.registerForm(ReportFormPage.GENERAL, this.form);
  }

  private _checkDeviationFoundValidation(reportType: ReportType | null): void {
    if (
      reportType === ReportType.IntegrityDeficiency ||
      reportType === ReportType.ReliabilityDeficiency
    ) {
      this.deviationFoundDate.addValidators([Validators.required]);
    } else {
      this.deviationFoundDate.setValidators(null);
    }

    this.deviationFoundDate.updateValueAndValidity({ emitEvent: false })
  }

  private _handleDamageMechanismValidation(): void {
    const reportType = this.reportType?.getRawValue();

    if (
      reportType === ReportType.IntegrityThreat
      || reportType === ReportType.ReliabilityThreat
      || reportType === ReportType.IntegrityDeficiency
      || reportType === ReportType.ReliabilityDeficiency
    ) {
      this.haveDamageMechanisms?.addValidators(Validators.required);
    }
    else {
      this.haveDamageMechanisms?.setValidators(null);
    }

    this.haveDamageMechanisms?.updateValueAndValidity({ emitEvent: false });
    this._updateDisciplineValidation();
  }

  private _handleTaskDeferalValidation(): void {
    if (this.isTaskDeferralReport()) {
      // For TDR: only Discipline and TA Required (shutDownRequired) are editable.
      // Other Finding Details fields are disabled (read-only) but keep any values
      // provided by the backend (e.g. PowerApp-created reports) so they can be displayed.
      this.findingType.disable({ emitEvent: false });
      this.form.get('retirementDate')?.disable({ emitEvent: false });
      this.form.get('ffsPerformed')?.disable({ emitEvent: false });
      this.form.get('ffsResult')?.disable({ emitEvent: false });
      this.form.get('deviationFoundDate')?.disable({ emitEvent: false });
    } else {
      // For non-TDR report types, enable Finding Details fields
      if (this.canEdit) {
        this.findingType?.enable({ emitEvent: false });
        this.form?.get('retirementDate')?.enable({ emitEvent: false });
        this.form?.get('ffsPerformed')?.enable({ emitEvent: false });
        this.form?.get('ffsResult')?.enable({ emitEvent: false });
        this.form?.get('deviationFoundDate')?.enable({ emitEvent: false });
      }
    }

    this.findingType?.updateValueAndValidity({ emitEvent: false });
    this.form?.get('retirementDate')?.updateValueAndValidity({ emitEvent: false });
    this.form?.get('ffsPerformed')?.updateValueAndValidity({ emitEvent: false });
    this.form?.get('ffsResult')?.updateValueAndValidity({ emitEvent: false });
    this.form?.get('deviationFoundDate')?.updateValueAndValidity({ emitEvent: false });
    this._updateDisciplineValidation();
  }

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(this.form, fieldName, this.displayNames);
  }

  private _toUtcCalendarDate(value: Date | string | null | undefined): Date | undefined {
    if (!value) {
      return undefined;
    }

    const parsed = value instanceof Date ? value : new Date(value);
    if (isNaN(parsed.getTime())) {
      return undefined;
    }

    return new Date(Date.UTC(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
      0,
      0,
      0,
      0,
    ));
  }

  onSubmit() {
    const value = this.form.getRawValue();

    if (this.form.valid) {
      const payload = GeneralTabDto.fromJS({
        ...value,
        id: this._generalTabId ?? undefined,
        findingType: value.findingType ?? undefined,
        ffsPerformed: value.ffsPerformed ?? undefined,
        ffsResult: value.ffsResult ?? undefined,
        closedOn: value.closedOn ?? undefined,
        initiator: this.initiatorFormControl.value ?? '',
        resolutionTargetDate: this._toUtcCalendarDate(value.resolutionTargetDate),
        retirementDate: this._toUtcCalendarDate(value.retirementDate),
        deviationFoundDate: this._toUtcCalendarDate(value.deviationFoundDate),
        damageMechanisms: this.damageMechanismsDataSource.data,
        uniqueId: value.uniqueId ?? undefined,
        discipline: value.discipline ?? '',
      });

      return this._handleSave(payload);
    } else if (!this.form.disabled) {
      this.form.markAllAsTouched();
      this.toastrService.info('Please fill all the required fields.');
    }
    return of(null);
  }

  removeDandFMode(): void {
    const itemsToRemove = this.selection.selected;
    this.damageMechanismsDataSource.data = this.damageMechanismsDataSource.data.filter(
      (item) => !itemsToRemove.includes(item)
    );
    this.tempTableData = [...this.damageMechanismsDataSource.data];
    this.selection.clear();
    this.haveDamageMechanisms.setValue(
      this.damageMechanismsDataSource.data.length > 0 ? true : null
    );
  }

  addDamageMechanismOrFailureMode(): void {
    const dref = this.dialog.open(AddDamageMechanismDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0' },
      data: { selectedItems: this.damageMechanismsDataSource.data },
    });

    dref.afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (result?.length) {
          this.damageMechanismsDataSource.data = result;
          this.tempTableData = [...this.damageMechanismsDataSource.data];
        }
        this.haveDamageMechanisms.setValue(this.damageMechanismsDataSource.data.length > 0 ? true : null);
      });
  }

  public get planApprovalDueDateDiff() {
    const daysForApproval = this.form.get('daysForApproval')?.value;
    return daysForApproval === 'Today' || daysForApproval != undefined && daysForApproval < 7;
  }

  private _initTriggers(): void {
    this.initiatorFormControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(GeneralTabComponent.EMAIL_AUTOCOMPLETE_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(initiator => this._getInitiatorDescription(initiator))
      )
      .subscribe();

    this.owner.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(GeneralTabComponent.EMAIL_AUTOCOMPLETE_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(owner => this._getOwnerDescription(owner, true))
      )
      .subscribe();

    this.resolutionTargetDate.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        this._store.setResolutionTargetDate(value);
      });

    this._route.paramMap
      .pipe(
        map((params) => params.get('id')),
        filter((id) => id != undefined && !this.form.dirty),
        switchMap((id) => {
          this._generalTabId = id;

          return this._getTabInfo();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {

        // console.log('val change', this.form.value);

        // Object.keys(this.form.controls).forEach(key => {
        //   const control = this.form.get(key);

        //   if (control?.invalid) {
        //     console.log('invalid', key, control);

        //   }
        // })


        const isFormValid = this.form.valid;

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });

        if (this._generalTabId) {
          Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key)

            if (control?.invalid && !control.dirty) {
              control.markAsTouched();
            }
          });
        }

        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this.onSubmit()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }
      });

    this._approvalFlow.flowEventsUpdated
      .pipe(
        switchMap(() => this._getTabInfo()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(res => this._store.setGeneral(res));

    this._approvalFlow.updateClosedOn  ///added by Ali
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.updateClosedOnDate();
      });

    this._bulkSave.saveComplete
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._approvalFlow.saveComplete.next();
      });

    // TODO: need to wait before policy checking completion
    combineLatest([
      this._store.general$,
      this._authService.roles$,
      this._policy.canEditForPage$(this.step),
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([general, roles, _]) => {
        const currentState = general?.currentState;
        const isAdmin = roles.includes(Role.Admin);

        if (!isAdmin && (
          currentState === ApprovalState.PlanApproved ||
          currentState === ApprovalState.ResolutionPlanCompleted ||
          currentState === ApprovalState.Closed ||
          currentState === ApprovalState.OutOfService ||
          currentState === ApprovalState.Cancelled
        )) {

          this.form.get('deviationFoundDate')?.disable({ emitEvent: false });
        } else if (
          (roles.includes(Role.Admin) || roles.includes(Role.Creator)) &&
          this.reportType?.value !== ReportType.TaskDeferral
        ) {
          this.form.get('deviationFoundDate')?.enable({ emitEvent: false });
        }

        this._updateReferencePermissions(general, roles);
      });

    this._policy.isTARequired$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((canEdit) => {
        this._taRequiredCanEdit = canEdit;
        this._applyTARequiredPolicy();
      });

    this._policy.canEditDamageMechanisms$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((canEdit) => this._updateDamageMechanismEditability(canEdit));

  }

  private _updateReferencePermissions(general: GeneralTabDto | null | undefined, roles: Role[]) {
    if (!general) {
      this.canAddReferencesOrTlr.set(false);
      return;
    }

    if (roles.includes(Role.Admin)) {
      this.canAddReferencesOrTlr.set(true);
      return;
    }

    if (
      general.currentState === ApprovalState.ResolutionPlanCompleted ||
      general.currentState === ApprovalState.ValidationAndVerificationCompleted ||
      general.currentState === ApprovalState.OutOfService ||
      general.currentState === ApprovalState.Cancelled ||
      general.currentState === ApprovalState.Closed
    ) {
      this.canAddReferencesOrTlr.set(false);
      return;
    }

    const initiatorEmail = this.initiatorFormControl.value?.toLowerCase();
    const ownerEmail = this.form.get('owner')?.value?.toLowerCase();

    if (!initiatorEmail || !ownerEmail) {
      this.canAddReferencesOrTlr.set(false);
      return;
    }

    const isInitiator = this._commonUserService.matchesWithField(initiatorEmail);
    const isOwner = this._commonUserService.matchesWithField(ownerEmail);
    this.canAddReferencesOrTlr.set(isInitiator || isOwner);
  }

  private _getTabInfo() {
    return this._generalTabId
      ? this.apiClient.generalTabGET2(this._generalTabId).pipe(
        map((record) => {
          // disable date-picker min value
          this.today = record.isPowerApp ? undefined : new Date();

          // filter discipline list
          this.disciplineOptions = DisciplineOptions.filter(item => !Boolean(item?.notAvailableForCreation) || item.title === record.discipline);

          const planApprovalDueDate = record.planApprovalDueDate ? moment(record.planApprovalDueDate) : null;
          let daysForApproval: string | number | undefined = undefined;

          if (
            planApprovalDueDate &&
            (
              record.reportType === ReportType.IntegrityDeficiency ||
              record.reportType === ReportType.ReliabilityDeficiency ||
              record.reportType === ReportType.TaskDeferral
            )
          ) {
            // "15-03-2026", "DD-MM-yyyy"
            const today = moment().set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0
            });

            const diff = planApprovalDueDate.diff(today, 'days');
            daysForApproval = diff < 0 ? 0 : (diff === 0 ? 'Today' : diff);
          }

          const payload = GeneralTabDto.fromJS({
            ...record,
            daysForApproval,
            created: utcToLocal(record.created)?.toDate(),
            retirementDate: utcToLocal(record.retirementDate)?.toDate(),
            planApprovalDueDate: utcToLocal(record.planApprovalDueDate)?.toDate(),
            resolutionTargetDate: utcToLocal(record.resolutionTargetDate)?.toDate(),
          });

          this._store.setGeneral(payload);

          this.isIntegrityThreat = record.reportType === ReportType.IntegrityThreat;
          this.isReliabilitThreat = record.reportType === ReportType.ReliabilityThreat;

          this.form.patchValue({
            ...payload,
            created: formatDate(payload.created),
            planApprovalDueDate: formatDate(payload.planApprovalDueDate),
          }, { emitEvent: false });

          this._handleDamageMechanismValidation();
          this._handleTaskDeferalValidation();
          this._updateDisciplineValidation();
          this._updateGeneralStepValidity();

          this.damageMechanismsDataSource.data = payload.damageMechanisms ?? [];
          this.tempTableData = [...this.damageMechanismsDataSource.data];
          this.haveDamageMechanisms.setValue(this.damageMechanismsDataSource.data.length > 0 ? true : null, { emitEvent: false });

          this._getInitiatorDescription(payload?.initiator ?? null).subscribe();
          // Keep backend owner as the source of truth on initial load.
          // We still call PeopleData to fill Owner Full Name and autocomplete options.
          this._getOwnerDescription(payload?.owner ?? null, false).subscribe();

          this._checkDeviationFoundValidation(payload.reportType ?? null);

          return payload;
        }),
        catchError(err => {
          return of(err);
        })
      )
      : of(null);
  }

  private _handleSave(payload: GeneralTabDto) {
    return this._generalTabId
      ? this.apiClient.generalTabPUT(this._generalTabId, payload).pipe(
        // map(() => this._generalTabId),
        switchMap(() => this._getTabInfo()),
        tap(() => {
          this._store.reset();
          // this._store.setGeneral(payload);

          // hide notification for closing
          if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
            this.toastrService.success('General Tab updated successfully.');
          }

          if (this._closeReport()) {
            this._navigationService.navigateToListPage();
          }
        }),
        catchError((err) => {
          this._store.reset();
          this.toastrService.error('General Tab was not updated successfully.');
          return of(err);
        })
      )
      : this.apiClient.generalTabPOST(payload).pipe(
        map((id) => id.toString()),
        // switchMap(() => this._getTabInfo()),
        tap((generalTabId) => {
          this._store.reset();

          if (this._closeReport()) {
            this._navigationService.navigateToListPage();
          } else {
            this._navigationService.navigateToRecord(generalTabId);
          }

          this.toastrService.success('General Tab saved successfully.');
        }),
        catchError((err) => {
          this._store.reset();
          this.toastrService.error('General Tab not saved successfully.');
          return of(err);
        })
      );
  }

  public refresh(): void {
    // TODO: Implement refresh logic
  }

  public export(): void {
    // TODO: Implement export logic
  }

  public updateClosedOnDate() {
    this.form.patchValue({ closedOn: moment().utc().toISOString() }, { emitEvent: false });

    const payload: ReportTask = {
      page: this.step,
      request: this.onSubmit().pipe(
        mergeMap(() => this._getTabInfo())
      )
    };

    this._bulkSave.add(payload);

    this._bulkSave.save();
  }

  private _updateOwnerFromCAI(res: GetPeopleDataByEmailResponseDto[]) {
    const preferredEmail = this.emailDescriptionService.getPreferredEmailForApprovalFlow(res);

    if (!preferredEmail) {
      return;
    }

    const currentOwner = this.owner.value?.trim().toLowerCase();
    if (currentOwner !== preferredEmail.toLowerCase()) {
      this.owner.setValue(preferredEmail, { emitEvent: false });
    }
  }

  private _getOwnerDescription(owner: string | null, allowOwnerNormalization = true) {
    this.loadingFields.update(fields => ({ ...fields, owner: true }));

    if (!owner || owner.trim() === '') {
      this.loadingFields.update(fields => ({ ...fields, owner: false }));
      this.form.get('ownerFullName')?.setValue(null, { emitEvent: false });
      this.emailAutocompleteOptions.update(options => ({
        ...options,
        owner: []
      }));
      return of([]);
    }

    return this.apiClient.email(owner).pipe(
      tap((ownerData) => {
        if (ownerData && ownerData.length > 0) {
          if (allowOwnerNormalization) {
            this._updateOwnerFromCAI(ownerData);
          }

          const description = ownerData.map(item => item.description).join('/n');
          this.form.get('ownerFullName')?.setValue(description, { emitEvent: false });

          const searchText = owner.trim().toLowerCase();
          const emailOptions = ownerData.flatMap(item => [
            item.email,
            item.preferredEmail,
            item.sharedEmail
          ])
            .filter((emailValue): emailValue is string =>
              !!emailValue && emailValue.trim() !== ''
            )
            .filter(emailValue =>
              emailValue.toLowerCase().includes(searchText)
            );

          const distinctEmailOptions = [...new Set(
            emailOptions.map(e => e.toLowerCase())
          )];

          this.emailAutocompleteOptions.update(options => ({
            ...options,
            owner: distinctEmailOptions
          }));

        } else {
          this.emailAutocompleteOptions.update(options => ({
            ...options,
            owner: []
          }));
        }
        this.loadingFields.update(fields => ({ ...fields, owner: false }));
      }),
      catchError(err => {
        console.error('Error fetching owner description:', err);
        this.emailAutocompleteOptions.update(options => ({
          ...options,
          owner: []
        }));
        this.loadingFields.update(fields => ({ ...fields, owner: false }));
        return of([]);
      }),
    );

  }

  private _getInitiatorDescription(initiator: string | null) {
    this.loadingFields.update(fields => ({ ...fields, initiator: true }));

    if (!initiator || initiator.trim() === '') {
      this.loadingFields.update(fields => ({ ...fields, initiator: false }));
      this.form.get('initiatorFullName')?.setValue(null, { emitEvent: false });
      this.emailAutocompleteOptions.update(options => ({
        ...options,
        initiator: []
      }));
      return of([]);
    }

    return this.apiClient.email(initiator).pipe(
      tap((initiatorData) => {
        if (initiatorData && initiatorData.length > 0) {
          const currentFullName = this.form.get('initiatorFullName')?.value;
          const shouldFillFromLookup = !currentFullName
            || `${currentFullName}`.trim() === ''
            || !!this.form.get('initiator')?.dirty;

          if (shouldFillFromLookup) {
            const fullName = this.emailDescriptionService.processOwnerDescriptions(initiatorData);
            this.form.get('initiatorFullName')?.setValue(fullName || null, { emitEvent: false });
          }

          const searchText = initiator.trim().toLowerCase();
          const emailOptions = initiatorData.flatMap(item => [
            item.email,
            item.preferredEmail,
            item.sharedEmail
          ])
            .filter((emailValue): emailValue is string =>
              !!emailValue && emailValue.trim() !== ''
            )
            .filter(emailValue =>
              emailValue.toLowerCase().includes(searchText)
            );

          const distinctEmailOptions = [...new Set(
            emailOptions.map(e => e.toLowerCase())
          )];

          this.emailAutocompleteOptions.update(options => ({
            ...options,
            initiator: distinctEmailOptions
          }));

        } else {
          this.emailAutocompleteOptions.update(options => ({
            ...options,
            initiator: []
          }));
        }
        this.loadingFields.update(fields => ({ ...fields, initiator: false }));
      }),
      catchError(err => {
        console.error('Error fetching initiator description:', err);
        this.emailAutocompleteOptions.update(options => ({
          ...options,
          initiator: []
        }));
        this.loadingFields.update(fields => ({ ...fields, initiator: false }));
        return of([]);
      }),
    );

  }

  getEmailAutocompleteOptions(fieldName: string): string[] {
    return this.emailAutocompleteOptions()[fieldName] || [];
  }

  onEmailAutocompleteSelected(event: MatAutocompleteSelectedEvent, emailFieldName: string, descriptionFieldName: string): void {
    const selectedEmail = event.option.value;
    const emailControl = this.form.get(emailFieldName);
    const descriptionControl = this.form.get(descriptionFieldName);

    if (!selectedEmail || !emailControl || !descriptionControl) {
      return;
    }

    this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: true }));

    this.apiClient.email(selectedEmail.trim()).pipe(
      tap((response) => {
        if (response && response.length > 0) {
          const descriptions = this.emailDescriptionService.processOwnerDescriptions(response);
          descriptionControl.setValue(descriptions);

          descriptionControl.markAsDirty();
          this.form.markAsDirty();
        } else {
          descriptionControl.setValue(null);
        }
        this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: false }));
      }),
      catchError(err => {
        console.warn(`Error fetching email details for ${emailFieldName}:`, err);
        this.loadingFields.update(fields => ({ ...fields, [emailFieldName]: false }));
        return of(null);
      }),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe();
  }

  clearDate(key: string) {
    this.form.get(key)?.setValue(null);
    this.form.get(key)?.markAsTouched();
    this.form.get(key)?.updateValueAndValidity();
  }
  getTypeLabel(type: string): string {
    const option = ReferenceTypeOptions.find(opt => opt.value === type);
    return option ? option.label : type;
  }

  // Reference
  addReference(): void {
    if (!this._generalTabId) {
      this.toastrService.error('Please save the general tab first');
      return;
    }

    if (!this.canAddReferencesOrTlr()) {
      this.toastrService.error('Only the initiator can add references in draft state');
      return;
    }

    const dref = this.dialog.open(ReferenceDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0' },
      data: {
        type: 'reference',
        generalTabId: this._generalTabId,
        existingData: this.referencesDataSource.data
      },
      disableClose: true,
    });

    dref.componentInstance.loading = this.isLoadingReferences;
    dref.componentInstance.onSubmit = (data: any, isEdit: boolean) => {
      const number = data.number || data.number;
      const existingRef = this.referencesDataSource.data.find(
        ref => ref.number === number && (!data.id || ref.id !== data.id)
      );

      if (existingRef) {
        this.toastrService.error('A reference with this number already exists');
        return;
      }

      this.isLoadingReferences.set(true);
      const url = `${environment.apiUrl}/api/Reference/tabs/${this._generalTabId}/references`;

      if (isEdit) {
        this.apiClient.referencesPUT(this._generalTabId!, data.id, new UpdateRequest({
          type: data.type,
          description: data.description,
          status: data.status,
          link: data.link,
        }))
          .pipe(
            catchError((error) => {
               const errorMessage = error?.error?.errors?.[0] || 'Failed to update reference';
              this.toastrService.error(errorMessage, 'Failed to update reference');
              this.isLoadingReferences.set(false);
              return of(null);
            })
          )
          .subscribe(() => {
            this.isLoadingReferences.set(false);
            this.toastrService.success('Reference updated successfully');
            this.loadReferences();
            dref.close();
          });
      } else {
        this._http.post<any[]>(url, data)
          .pipe(
            catchError((error) => {
              const errorMessage = error?.error?.errors?.[0] || 'Failed to add reference';
              this.toastrService.error(errorMessage, 'Failed to add reference');
              this.isLoadingReferences.set(false);
              return of(null);
            })
          )
          .subscribe((response: any) => {
            this.isLoadingReferences.set(false);
            if (response !== null) {
              this.toastrService.success('Reference added successfully');
              this.loadReferences();
              dref.close();
            }
          });
      }
    };
  }

  onReferenceClick(reference: any): void {
    if (!this._generalTabId) {
      return;
    }

    const dref = this.dialog.open(ReferenceDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0' },
      data: {
        type: 'reference',
        generalTabId: this._generalTabId,
        editData: reference,
        existingData: this.referencesDataSource.data.filter(r => r.id !== reference.id)
      },
      disableClose: true,
    });

    dref.componentInstance.loading = this.isLoadingReferences;
    dref.componentInstance.onSubmit = (data: any, isEdit: boolean) => {
      const existingRef = this.referencesDataSource.data.find(
        ref => ref.number === data.number && ref.id !== data.id
      );

      if (existingRef) {
        this.toastrService.error('A reference with this number already exists');
        return;
      }

      this.isLoadingReferences.set(true);

      this.apiClient.referencesPUT(this._generalTabId!, data.id, new UpdateRequest({
        type: data.type,
        description: data.description,
        status: data.status,
        link: data.link,
      }))
        .pipe(
          catchError((error) => {
             const errorMessage = error?.error?.errors?.[0] || 'Failed to update reference';
            this.toastrService.error(errorMessage, 'Failed to update reference');
            this.isLoadingReferences.set(false);
            return of(null);
          })
        )
        .subscribe((response: any) => {
          this.isLoadingReferences.set(false);
          this.toastrService.success('Reference updated successfully');
          this.loadReferences();
          dref.close();
        });
    };
  }

  deleteReferences(): void {
    if (!this.referencesSelection.hasValue() || !this._generalTabId) {
      return;
    }

    this.isLoadingReferences.set(true);
    const selectedReferences = this.referencesSelection.selected;
    const deleteObservables = selectedReferences.map(ref =>
      this.apiClient.referencesDELETE(this._generalTabId!, ref.id)
        .pipe(
          catchError((error) => {
            this.toastrService.error(error, 'Failed to delete reference');
            return of(null);
          })
        )
    );

    combineLatest(deleteObservables).subscribe(() => {
      this.isLoadingReferences.set(false);
      this.toastrService.success('References deleted successfully');
      this.referencesSelection.clear();
      this.loadReferences();
    });
  }

  loadReferences(): void {
    if (!this._generalTabId) {
      return;
    }

    const url = `${environment.apiUrl}/api/Reference/tabs/${this._generalTabId}/references`;
    this._http.get<any[]>(url)
      .pipe(
        catchError((error) => {
          this.toastrService.error(error, 'Failed to load references');
          return of([]);
        })
      )
      .subscribe((data: any) => {
        this.referencesDataSource.data = Array.isArray(data) ? data : [];
      });
  }

  isAllReferencesSelected(): boolean {
    const numSelected = this.referencesSelection.selected.length;
    const numRows = this.referencesDataSource.data.length;
    return numSelected === numRows;
  }

  masterToggleReferences(): void {
    this.isAllReferencesSelected()
      ? this.referencesSelection.clear()
      : this.referencesDataSource.data.forEach(row => this.referencesSelection.select(row));
  }

  // TLR
  addTlrEntry(): void {
    if (!this._generalTabId) {
      this.toastrService.error('Please save the general tab first');
      return;
    }

    if (!this.canAddReferencesOrTlr()) {
      this.toastrService.error('Only the initiator can add TLR entries in draft state');
      return;
    }

    const dref = this.dialog.open(ReferenceDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0' },
      data: {
        type: 'tlr',
        generalTabId: this._generalTabId,
        existingData: this.tlrEntriesDataSource.data
      },
      disableClose: true,
    });

    dref.componentInstance.loading = this.isLoadingTlr;
    dref.componentInstance.onSubmit = (data: any, isEdit: boolean) => {
      const existingTlr = this.tlrEntriesDataSource.data.find(
        tlr => tlr.tag === data.tag && (!data.id || tlr.id !== data.id)
      );

      if (existingTlr) {
        this.toastrService.error('A TLR entry with this tag already exists');
        return;
      }

      this.isLoadingTlr.set(true);

      const url = `${environment.apiUrl}/api/Reference/tabs/${this._generalTabId}/tlr-entries`;

      if (isEdit) {
        this.apiClient.tlrEntriesPUT(this._generalTabId!, data.id, data)
          .pipe(
            catchError((error) => {
               const errorMessage = error?.error?.errors?.[0] || 'Failed to update TLR entry';
              this.toastrService.error(errorMessage, 'Failed to update TLR entry');
              this.isLoadingTlr.set(false);
              return of(null);
            })
          )
          .subscribe((response) => {
            this.isLoadingTlr.set(false);
            if (response !== null) {
              this.toastrService.success('TLR Entry updated successfully');
              this.loadTlrEntries();
              dref.close();
            }
          });
      } else {
        this._http.post<any[]>(url, data)
          .pipe(
            catchError((error) => {
               const errorMessage = error?.error?.errors?.[0] || 'Failed to add TLR entry';
              this.toastrService.error(errorMessage, 'Failed to add TLR entry');
              this.isLoadingTlr.set(false);
              return of(null);
            })
          )
          .subscribe((response: any) => {
            this.isLoadingTlr.set(false);
            if (response !== null) {
              this.toastrService.success('TLR Entry added successfully');
              this.loadTlrEntries();
              dref.close();
            }
          });
      }
    };
  }

  isTLRInActive = computed(() => {
    const currentState = this._general()?.currentState;

    if (this._authService.isInRole(Role.Admin)) {
      return false;
    }

    return currentState === ApprovalState.ResolutionPlanCompleted ||
      currentState === ApprovalState.Closed ||
      currentState === ApprovalState.Cancelled ||
      currentState === ApprovalState.OutOfService
  });

  onTlrClick(tlr: any): void {
    if ( !this._generalTabId || this.isTLRInActive() ) {
      return;
    }

    const dref = this.dialog.open(ReferenceDialogComponent, {
      width: '400px',
      height: '100vh',
      position: { right: '0' },
      data: {
        type: 'tlr',
        generalTabId: this._generalTabId,
        editData: tlr,
        existingData: this.tlrEntriesDataSource.data.filter(t => t.id !== tlr.id)
      },
      disableClose: true,
    });

    dref.componentInstance.loading = this.isLoadingTlr;
    dref.componentInstance.onSubmit = (data: any, isEdit: boolean) => {
      // Validate duplicate tag
      const existingTlr = this.tlrEntriesDataSource.data.find(
        t => t.tag === data.tag && t.id !== tlr.id
      );


      if (existingTlr) {
        this.toastrService.error('A TLR entry with this tag already exists');
        return;
      }

      this.isLoadingTlr.set(true);
      data.id = tlr.id;

      this.apiClient.tlrEntriesPUT(this._generalTabId!, data.id, data)
        .pipe(
          catchError((error) => {
            const errorMessage = error?.error?.errors?.[0] || 'Failed to update TLR entry';
            this.toastrService.error(errorMessage, 'Failed to update TLR entry');
            this.isLoadingTlr.set(false);
            return of(null);
          })
        )
        .subscribe((response) => {
          this.isLoadingTlr.set(false);
          this.toastrService.success('TLR Entry updated successfully');
          this.loadTlrEntries();
          dref.close();
        });
    };
  }

  deleteTlrEntries(): void {
    if (!this.tlrEntriesSelection.hasValue() || !this._generalTabId) {
      return;
    }

    this.isLoadingTlr.set(true);
    const selectedEntries = this.tlrEntriesSelection.selected;
    const deleteObservables = selectedEntries.map(entry =>
      this.apiClient.tlrEntriesDELETE(this._generalTabId!, entry.id)
        .pipe(
          catchError((error) => {
            const errorMessage = error?.error?.errors?.[0] || 'Failed to delete TLR entry';
            this.toastrService.error(errorMessage, 'Failed to delete TLR entry');
            return of(null);
          })
        )
    );

    combineLatest(deleteObservables).subscribe(() => {
      this.isLoadingTlr.set(false);
      this.toastrService.success('TLR Entries deleted successfully');
      this.tlrEntriesSelection.clear();
      this.loadTlrEntries();
    });
  }

  getTlrTypeLabel(value: string): string {
    return (this.tlrTypesMap.get(value) as string) || value;
  }

  getTaScopeLabel(value: string): string {
    return (this.taScopesMap.get(value) as string) || value;
  }

  getEquipmentProcessServiceLabel(value: string): string {
    return (this.equipmentProcessServicesMap.get(value) as string) || value;
  }

  loadTlrEntries(): void {
    if (!this._generalTabId) {
      return;
    }

    const url = `${environment.apiUrl}/api/Reference/tabs/${this._generalTabId}/tlr-entries`;
    this._http.get<any[]>(url)
      .pipe(
        catchError((error) => {
          console.error('Error loading TLR entries:', error);
          return of([]);
        })
      )
      .subscribe((data: any) => {
        this.tlrEntriesDataSource.data = Array.isArray(data) ? data : [];
      });
  }

  isAllTlrEntriesSelected(): boolean {
    const numSelected = this.tlrEntriesSelection.selected.length;
    const numRows = this.tlrEntriesDataSource.data.length;
    return numSelected === numRows;
  }

  masterToggleTlrEntries(): void {
    this.isAllTlrEntriesSelected()
      ? this.tlrEntriesSelection.clear()
      : this.tlrEntriesDataSource.data.forEach(row => this.tlrEntriesSelection.select(row));
  }

  filters: Record<string, FilterResponse | null> = {};
  tempTableData: any[] = [];

  applyFilter(res: FilterResponse | null, column: string) {
    this.filters[column] = res;
    this.damageMechanismsDataSource.data = filterTable(this.tempTableData, column, res);
  }

  isActive(column: string) {
    return this.filters[column] != null;
  }
}
